import { z } from 'zod';
import { SongId } from './ids.js';
import {
  Money,
  SourceCode,
  IdempotencyKey,
  CurrencyCode,
  MinorUnits,
} from './primitives.js';
import { AudienceMode } from './enums.js';

/** Public API request/response DTOs for the M2–M6 audience flow (§28.3). */

/** POST /v1/public/gigs/{publicCode}/audience-sessions */
export const CreateAudienceSessionResponse = z.object({
  audienceSessionId: z.string(),
  expiresAt: z.string(),
});
export type CreateAudienceSessionResponse = z.infer<typeof CreateAudienceSessionResponse>;

/** POST /v1/public/gigs/{publicCode}/song-requests */
export const CreateSongRequestBody = z.object({
  songId: SongId,
  nickname: z.string().max(30).optional(),
  note: z.string().max(280).optional(),
  sourceCode: SourceCode.optional(),
  idempotencyKey: IdempotencyKey,
});
export type CreateSongRequestBody = z.infer<typeof CreateSongRequestBody>;

/** POST /v1/public/gigs/{publicCode}/suggestions */
export const CreateSuggestionBody = z.object({
  category: z.string().max(40).optional(),
  message: z.string().min(1).max(280),
  nickname: z.string().max(30).optional(),
  relatedSongTitle: z.string().max(200).optional(),
  sourceCode: SourceCode.optional(),
  /**
   * Hidden honeypot field (§19.3, §25.4). A human leaves it empty; a bot fills it. It is accepted
   * (not rejected at parse) so the response stays indistinguishable — moderation silently blocks a
   * filled value and the content is never forwarded, rather than revealing detection via a 400.
   */
  website: z.string().max(200).optional(),
  idempotencyKey: IdempotencyKey,
});
export type CreateSuggestionBody = z.infer<typeof CreateSuggestionBody>;

/** POST /v1/public/gigs/{publicCode}/improv-briefs */
export const CreateImprovBriefBody = z.object({
  profileVersion: z.number().int().positive(),
  audienceMode: AudienceMode,
  rawChoices: z.record(z.string(), z.string()),
  topic: z.string().max(200).optional(),
  sourceCode: SourceCode.optional(),
  idempotencyKey: IdempotencyKey,
});
export type CreateImprovBriefBody = z.infer<typeof CreateImprovBriefBody>;

/** Generic interaction acknowledgement — never promises performance (§18.4, §20.15). */
export const InteractionAck = z.object({
  interactionId: z.string(),
  acknowledgement: z.string(),
});
export type InteractionAck = z.infer<typeof InteractionAck>;

/** POST /v1/public/gigs/{publicCode}/tips/checkout */
export const CreateTipCheckoutBody = z.object({
  amount: Money.optional(),
  currency: CurrencyCode.optional(),
  amountMinor: MinorUnits.positive().optional(),
  message: z.string().max(280).optional(),
  sourceCode: SourceCode.optional(),
  idempotencyKey: IdempotencyKey,
});
export type CreateTipCheckoutBody = z.infer<typeof CreateTipCheckoutBody>;

export const CreateTipCheckoutResponse = z.object({
  checkoutUrl: z.string().url(),
  tipId: z.string(),
});
export type CreateTipCheckoutResponse = z.infer<typeof CreateTipCheckoutResponse>;

/** POST /v1/public/gigs/{publicCode}/telemetry — fire-and-forget analytics (§26). */
export const TelemetryBody = z.object({
  events: z
    .array(
      z.object({
        name: z.string().min(1).max(60),
        at: z.string().datetime({ offset: false }),
        sourceCode: SourceCode.optional(),
        props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
      }),
    )
    .max(50),
});
export type TelemetryBody = z.infer<typeof TelemetryBody>;
